import clsx from "clsx";
import type { KeyboardEvent, ReactNode } from "react";
import {
  WORKBENCH_CARD_SHELL_INTERACTIVE_CLASS,
  WORKBENCH_CARD_SHELL_SELECTED_CLASS,
  WORKBENCH_CARD_STATUS_ADVANCING_CLASS,
  WORKBENCH_CARD_TIMESTAMP_CLASS,
} from "@/components/ui/workbench";

/**
 * Shared row chrome for Mission Control lists (Tasks, Missions, Artifacts).
 *
 * Slot layout:
 *   [icon]  {title} [titleBadges]              {timestamp}
 *           {subtitle}                           {status}
 *           {children}                           {actions}
 */
export interface MissionControlCardProps {
  /** Left avatar or icon. */
  icon?: ReactNode;
  /** Primary heading (plain text or rich content). */
  title: ReactNode;
  /** Inline badges beside the title (version, kind, priority — not primary status). */
  titleBadges?: ReactNode;
  /** Context line under the title (mission link, type/stage row, etc.). */
  subtitle?: ReactNode;
  /** Extra body content (summary, errors). */
  children?: ReactNode;
  /** Top-right relative time, usually prefixed with entity type. */
  timestamp?: ReactNode;
  /** Primary status badge — always rendered below the timestamp on the right. */
  status?: ReactNode;
  /** Bottom-right action buttons (download, external links). */
  actions?: ReactNode;
  /** Highlight when selected (task list). */
  selected?: boolean;
  /**
   * When set (0–100), fills the card background from the left with a subtle tint
   * proportional to completion. Used by mission rows instead of a progress bar.
   */
  progressPct?: number;
  /** Use the completed (green) tint rather than in-progress (indigo). */
  progressComplete?: boolean;
  /** Brief highlight when progress fill advances (set by parent when WS updates land). */
  progressAdvancing?: boolean;
  /** Brief highlight on the status badge when lifecycle status changes. */
  statusAdvancing?: boolean;
  /** Tighter padding for embedded/thread cards. */
  compact?: boolean;
  className?: string;
  "data-testid"?: string;
  /** Deep-link focus target (`<subject_type>:<id>`) for `useDeepLinkFocus`. */
  "data-focus-ref"?: string;
  onClick?: () => void;
  /** Render as `<button>` (missions) or `<div role="button">` (tasks, artifacts). */
  as?: "button" | "div";
  /** Overrides accessible name when `title` is not plain text. */
  ariaLabel?: string;
}

export function MissionControlCard({
  icon,
  title,
  titleBadges,
  subtitle,
  children,
  timestamp,
  status,
  actions,
  selected = false,
  progressPct,
  progressComplete = false,
  progressAdvancing = false,
  statusAdvancing = false,
  compact = false,
  className,
  "data-testid": testId,
  "data-focus-ref": focusRef,
  onClick,
  as = "div",
  ariaLabel,
}: MissionControlCardProps) {
  const accessibleName =
    ariaLabel ?? (typeof title === "string" ? title : undefined);
  const hasRightColumn = Boolean(timestamp || status || actions);
  const clampedProgress =
    progressPct == null
      ? null
      : Math.max(0, Math.min(100, Math.round(progressPct)));
  const showProgressFill = clampedProgress != null && clampedProgress > 0;

  const shellClass = clsx(
    "relative w-full overflow-hidden text-left transition-colors cursor-pointer rounded-xl focus:outline-none",
    WORKBENCH_CARD_SHELL_INTERACTIVE_CLASS,
    compact ? "px-3 py-2.5" : "px-5 py-3",
    selected && WORKBENCH_CARD_SHELL_SELECTED_CLASS,
    className,
  );

  const progressFill = showProgressFill ? (
    <div
      aria-hidden
      className={clsx(
        "pointer-events-none absolute inset-y-0 left-0 transition-[width] duration-500 ease-out",
        progressComplete
          ? progressAdvancing
            ? "bg-emerald-500/15 dark:bg-emerald-500/[0.12]"
            : "bg-emerald-500/[0.08] dark:bg-emerald-500/[0.04]"
          : progressAdvancing
            ? "bg-indigo-500/15 dark:bg-indigo-500/[0.14]"
            : "bg-indigo-500/10 dark:bg-indigo-500/[0.08]",
      )}
      style={{ width: `${clampedProgress}%` }}
    />
  ) : null;

  const handleKeyDown = (e: KeyboardEvent) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      onClick?.();
    }
  };

  const body = (
    <div
      className={clsx(
        "relative z-[1] flex w-full gap-3",
        compact ? "items-center" : "items-start",
      )}
    >
      {icon ? <div className={clsx("shrink-0", !compact && "mt-0.5")}>{icon}</div> : null}

      <div
        className={clsx(
          "grid min-w-0 flex-1 grid-cols-[minmax(0,1fr)_auto] gap-x-3",
          compact ? "items-center gap-y-0" : "items-start gap-y-1",
        )}
      >
        <div className="min-w-0 overflow-hidden">
          <div className="flex min-w-0 flex-wrap items-center gap-x-2 gap-y-1">
            <span
              className={clsx(
                "min-w-0 shrink truncate font-medium text-slate-900 dark:text-slate-100",
                compact ? "text-xs" : "text-sm",
              )}
            >
              {title}
            </span>
            {titleBadges ? (
              <span className="flex shrink-0 flex-wrap items-center gap-1.5">
                {titleBadges}
              </span>
            ) : null}
          </div>

          {subtitle}

          {children}
        </div>

        {hasRightColumn ? (
          <div
            className={clsx(
              "flex shrink-0 flex-col items-end gap-1",
              compact ? "justify-center self-center" : "pt-0.5",
            )}
          >
            {timestamp ? (
              <div className={WORKBENCH_CARD_TIMESTAMP_CLASS}>
                {timestamp}
              </div>
            ) : null}
            {status ? (
              <div
                className={clsx(
                  "flex shrink-0 items-center justify-end rounded-md transition-colors duration-500",
                  statusAdvancing && WORKBENCH_CARD_STATUS_ADVANCING_CLASS,
                )}
              >
                {status}
              </div>
            ) : null}
            {actions ? (
              <div className="flex flex-wrap items-center justify-end gap-1.5">
                {actions}
              </div>
            ) : null}
          </div>
        ) : null}
      </div>
    </div>
  );

  const content = (
    <>
      {progressFill}
      {body}
    </>
  );

  const selectionProps =
    onClick && selected
      ? { "aria-pressed": true as const }
      : {};

  if (as === "button") {
    return (
      <button
        type="button"
        data-testid={testId}
        data-focus-ref={focusRef}
        onClick={onClick}
        className={shellClass}
        aria-label={accessibleName}
        {...selectionProps}
      >
        {content}
      </button>
    );
  }

  return (
    <div
      role="button"
      tabIndex={0}
      data-testid={testId}
      data-focus-ref={focusRef}
      onClick={onClick}
      onKeyDown={handleKeyDown}
      className={shellClass}
      aria-label={accessibleName}
      {...selectionProps}
    >
      {content}
    </div>
  );
}
